import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { multiStep } from '../../MultiStepWizard/multiStep'
import { SelectedImage } from 'types'
import { Provider } from 'components/ImagePickerModal/Types'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Form } from 'components/Wizards/Form/Form'
import DesignMarketplaceWizardStyles from '../DesignMarketplaceWizard.module.scss'
import Styles from './SelectImages.module.scss'
import { Formik, useFormikContext } from 'formik'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'
import { ImageLink } from 'blaze-ui/icons'
import { useEffect, useState } from 'react'
import { useConfirm } from '../../DesignWizard/AddPhoto/useConfirm'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getTemplatePreviewCloudinaryUrl } from 'components/Design/utils/getTemplatePreviewCloudinaryUrl'
import { MessageType } from 'antd/lib/message'
import { Values } from '../../DesignWizard/Types'
import { MobileImageButton } from 'components/Design/MobileImageButtons/MobileImageButtons'
import { Background } from 'blaze-ui/icons/32'
import Camera from 'blaze-ui/icons/20/Camera'
import { RecentUploads } from 'components/ImagePickerModal/Uploads/RecentUploads'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'

interface Props extends StepProps {
  isLoading?: boolean
  onImageChange: (image: SelectedImage) => void
  setCanRemoveBackground: (value: boolean) => void
  createDesign: (values: Values) => Promise<void>
}

interface ContentProps extends Props {
  isUploading: boolean
  setIsUploading: (value: boolean) => void
  uploadedImageBase64: string
}

export const SelectImageMobile = multiStep<Props>((props) => {
  const { initialValues, onImageChange, schema, onSubmit } = props
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedImageBase64, setUploadedImageBase64] = useState('')
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id

  let uploadingToast: MessageType

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
    })

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault() }, { assert: false })}
    >
      {({ setFieldValue }) => (
        <ImagePickerProvider
          defaultProvider="uploads"
          maxCount={1}
          accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
          uploadImages={async (files) => {
            if (!uploadingToast) uploadingToast = showToastMessage('Uploading image...', 'generating', undefined, 0)
            const uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId)
            uploadingToast?.()

            if (!uploadedImages) return []
            return uploadedImages.map((file) => ({ id: file.id, url: file.source_url } as SelectedImage))
          }}
          initialQuery={initialValues.topic || ''}
          onUploadStart={async (files) => {
            const base64Url = (await toBase64(files[0])) as string
            setUploadedImageBase64(base64Url)
            setIsUploading(true)
          }}
          onConfirm={async (images) => {
            if (images[0]?.id) setFieldValue('initial_media_file_id', images[0].id)
            setIsUploading(false)
            onImageChange(images[0])
          }}
          hiddenProviders={new Set<Provider>(['gifs', 'ai'])}
        >
          <Content
            {...props}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadedImageBase64={uploadedImageBase64}
          />
        </ImagePickerProvider>
      )}
    </Formik>
  )
})

const Content = observer(
  multiStep<ContentProps>(
    ({
      schema,
      onImageChange,
      setIsUploading,
      setCanRemoveBackground,
      uploadedImageBase64,
      createDesign,
      initialValues,
    }) => {
      const imagePickerContext = useImagePickerContext()
      const { isSubmitting, submitForm, setFieldValue, isValid } = useFormikContext()
      const [isGenerating, setIsGenerating] = useState(false)
      const [imageFromAllUploads, setImageFromAllUploads] = useState<SelectedImage>()
      const { confirm } = useConfirm()
      const { drawersStore } = useRootStore()

      const { provider, selectedImages, isUploading } = imagePickerContext

      const handleSubmit = () => {
        if (selectedImages[0]?.id) setFieldValue('initial_media_file_id', selectedImages[0].id)

        // Need to add a small timeout before submitting right after the required field is set in the line above,
        // otherwise the submit validation doesn't pass and the step is not submitted
        setTimeout(() => {
          submitForm()
          setCanRemoveBackground(true)
        }, 100)
      }

      useEffect(() => {
        if (selectedImages[0]) onImageChange(selectedImages[0])
      }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

      const imageUrl = selectedImages[0]?.source_url || selectedImages[0]?.url
      const url = imageUrl ? getTemplatePreviewCloudinaryUrl(imageUrl, { height: 350 }) : uploadedImageBase64
      const isDisabled = isSubmitting || isUploading || isGenerating

      const handleStartWithBlankCanvas = async () => {
        setIsGenerating(true)
        await createDesign({
          ...initialValues,
          initial_media_file_id: undefined,
          filename: 'Blank Canvas',
        }).finally(() => {
          setIsGenerating(false)
        })
      }

      const selectImageWithDrawer = (defaultProvider: Provider) => {
        const hiddenProviders = new Set<Provider>(['ai', 'link', 'stock', 'gifs', 'uploads'])
        hiddenProviders.delete(defaultProvider)

        drawersStore.openDrawer(ImagePickerDrawer, {
          ...imagePickerContext,
          hiddenProviders,
          confirmLabel: 'Use Image',
          defaultProvider,
          onConfirm: async (images) => {
            setImageFromAllUploads(images[0])
            imagePickerContext.onConfirm(images, imagePickerContext)
            drawersStore.closeDrawer()
          },
          onCancel: () => {
            drawersStore.closeDrawer()
          },
        })
      }

      return (
        <Form.Root>
          <Form.Section>
            <div
              className={cx(DesignMarketplaceWizardStyles.container, {
                [DesignMarketplaceWizardStyles.selectImageContainer]: !isUploading,
              })}
            >
              {isUploading ? (
                <LoadingShimmerer className={Styles.selectedImage}>
                  <img alt="selected" src={url} />
                </LoadingShimmerer>
              ) : (
                <>
                  <div className={Styles.fieldContainer}>
                    <BaseField label="Upload your image">
                      <div className={Styles.imagesFieldContainer}>
                        <div className={Styles.providersButtons}>
                          <MobileImageButton
                            label="Photos"
                            icon={() => <Background size={30} />}
                            variant="secondary"
                            onConfirm={(images) =>
                              imagePickerContext.onConfirm(images as SelectedImage[], imagePickerContext)
                            }
                          />
                          <MobileImageButton label="Camera" icon={Camera} variant="secondary" capture="environment" />
                          <MobileImageButton
                            label="Image Link"
                            icon={() => <ImageLink size={30} />}
                            variant="secondary"
                            onPress={() => selectImageWithDrawer('link')}
                          />
                        </div>
                      </div>
                    </BaseField>
                  </div>

                  <div className={Styles.providers}>
                    <RecentUploads
                      onOpenAll={() => selectImageWithDrawer('uploads')}
                      headingLevel={5}
                      label="Or use images already uploaded"
                      size="md"
                      showSelected
                      displayType="grid"
                      customImages={imageFromAllUploads ? [imageFromAllUploads] : []}
                    />
                  </div>
                </>
              )}
            </div>
          </Form.Section>
          <Form.Actions isSticky>
            <Form.PrimaryActions>
              <Button
                variant="tertiary"
                size="lg"
                fullWidth
                isDisabled={isDisabled}
                onPress={handleStartWithBlankCanvas}
              >
                Or start with a Blank Canvas
              </Button>
              <Button
                isDisabled={isDisabled || !selectedImages[0]}
                size="lg"
                fullWidth
                onPress={async () => {
                  if (!isValid) {
                    submitForm()
                    return
                  }

                  if (provider === 'link') {
                    setIsUploading(true)
                    await confirm() // this will call the link provider's useConfirm hook
                    setIsUploading(false)
                  }

                  handleSubmit()
                }}
              >
                {schema.describe().meta.submitLabel}
              </Button>
            </Form.PrimaryActions>
          </Form.Actions>
        </Form.Root>
      )
    },
  ),
)
